export const ICOMX_PROTOCOL_VERSION = 1 as const;

export const ICOMX_HOST_COMMAND_TYPES = [
  'comx.host.open_contact',
  'comx.host.start_direct_conversation',
  'comx.host.open_last_conversation',
  'comx.host.upsert_contact',
] as const;

export const ICOMX_IFRAME_EVENT_TYPES = [
  'comx.iframe.ready',
  'comx.iframe.ack',
  'comx.iframe.error',
  'comx.iframe.contact_opened',
  'comx.iframe.conversation_opened',
  'comx.iframe.popup_blocked',
  'comx.iframe.popup_closed',
] as const;

export type IcomxHostCommandType = (typeof ICOMX_HOST_COMMAND_TYPES)[number];
export type IcomxIframeEventType = (typeof ICOMX_IFRAME_EVENT_TYPES)[number];

export interface IcomxOpenContactPayload {
  contactUuid: string;
}

export interface IcomxSelectedTelecom {
  system: 'email' | 'phone';
  value: string;
}

export interface IcomxOpenLastConversationPayload extends IcomxOpenContactPayload {
  fallbackToDirect?: boolean;
  selectedTelecom?: IcomxSelectedTelecom;
}

export interface IcomxManagedContactPayload {
  uuid?: string;
  integration_id?: string;
  first_name: string;
  last_name: string;
  phone?: string;
  email?: string;
}

export interface IcomxUpsertContactPayload {
  contact: IcomxManagedContactPayload;
}

export interface IcomxAckPayload {
  acceptedType?: IcomxHostCommandType;
}

export interface IcomxErrorPayload {
  code?: string;
  message?: string;
}

export interface IcomxContactOpenedPayload {
  contactUuid: string;
}

export interface IcomxConversationOpenedPayload {
  conversationUuid: string;
  created?: boolean;
}

export interface IcomxReadyPayload {
  [key: string]: unknown;
}

export interface IcomxPopupLifecyclePayload {
  flow?: string;
  provider?: string;
  [key: string]: unknown;
}

export interface IcomxHostCommandPayloadMap {
  'comx.host.open_contact': IcomxOpenContactPayload;
  'comx.host.start_direct_conversation': IcomxOpenContactPayload;
  'comx.host.open_last_conversation': IcomxOpenLastConversationPayload;
  'comx.host.upsert_contact': IcomxUpsertContactPayload;
}

export interface IcomxIframeEventPayloadMap {
  'comx.iframe.ready': IcomxReadyPayload;
  'comx.iframe.ack': IcomxAckPayload;
  'comx.iframe.error': IcomxErrorPayload;
  'comx.iframe.contact_opened': IcomxContactOpenedPayload;
  'comx.iframe.conversation_opened': IcomxConversationOpenedPayload;
  'comx.iframe.popup_blocked': IcomxPopupLifecyclePayload;
  'comx.iframe.popup_closed': IcomxPopupLifecyclePayload;
}

export interface IcomxEnvelope<TType extends string, TPayload> {
  version: typeof ICOMX_PROTOCOL_VERSION;
  type: TType;
  requestId: string;
  payload: TPayload;
}

export type IcomxHostCommandEnvelope<TType extends IcomxHostCommandType = IcomxHostCommandType> =
  IcomxEnvelope<TType, IcomxHostCommandPayloadMap[TType]>;

export type IcomxIframeEventEnvelope<TType extends IcomxIframeEventType = IcomxIframeEventType> =
  IcomxEnvelope<TType, IcomxIframeEventPayloadMap[TType]>;

export interface IcomxTerminalEventMap {
  'comx.host.open_contact': IcomxIframeEventEnvelope<'comx.iframe.contact_opened'>;
  'comx.host.start_direct_conversation': IcomxIframeEventEnvelope<'comx.iframe.conversation_opened'>;
  'comx.host.open_last_conversation': IcomxIframeEventEnvelope<'comx.iframe.conversation_opened'>;
  'comx.host.upsert_contact': IcomxIframeEventEnvelope<'comx.iframe.contact_opened'>;
}

export type IcomxCommandResult<TType extends IcomxHostCommandType> = IcomxTerminalEventMap[TType];

export interface IcomxBridgeState {
  iframeOrigin: string | null;
  hasIframeWindow: boolean;
  isReady: boolean;
  pendingCount: number;
  lastEvent: IcomxIframeEventEnvelope | null;
}

export type IcomxSecurityRejectionReason =
  | 'unknown-origin'
  | 'unknown-source'
  | 'invalid-envelope'
  | 'unsupported-version'
  | 'unknown-type'
  | 'missing-request-id'
  | 'malformed-payload'
  | 'unexpected-request';

export interface IcomxSecurityRejection {
  reason: IcomxSecurityRejectionReason;
  event?: MessageEvent<unknown>;
  details?: unknown;
}

export interface IcomxBridgeCommandError extends Error {
  code?: string;
  requestId?: string;
}
